import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ErroApi, api } from './api';
import { rotulo } from './formato';
import { useSessao } from './sessao';
import { apoioDaAmostra, daApi, tituloDaAmostra } from './NomeDaAmostra';
import { Icone } from './Icone';
import { LeitorQr } from './LeitorQr';
import { Aviso, Dialogo, ErroDaApi, useEstreito } from './ui';

/**
 * A busca do cabeçalho — `Ctrl+K`, como no protótipo.
 *
 * Ela procura em TRÊS lugares e não em todos: amostra, lote de recebimento
 * e equipamento. O motivo é chato e importante — são os três que têm tela
 * para onde ir. Listar uma OS aqui hoje devolveria um resultado que não
 * abre, e resultado que não abre ensina o técnico a não usar a busca.
 * Quando a tela de OS existir, ela entra aqui na mesma hora.
 *
 * A consulta sai depois de 250 ms parado e cancela a anterior: no galpão a
 * conexão é ruim, e disparar uma requisição por tecla digitada é o jeito
 * mais rápido de a busca ficar mais lenta que a lista.
 *
 * Desde a H1 (onda 295) amostras e lotes são do LabManager, e o sondador não
 * os lê (D6): para quem não tem `amostra.ver` a busca nem pergunta por eles —
 * perguntar seriam dois 403 a cada tecla, e um resultado que, se viesse,
 * abriria uma recusa. Equipamentos são do espaço Comum e continuam para
 * todos.
 *
 * ── O QR mora aqui, no celular (onda 308, spec 06 §5.4) ──────────────────
 *
 * No celular a busca ganha o botão «Ler QR», dentro do campo, à direita: a
 * etiqueta da amostra é o jeito mais curto de achá-la, e o técnico está com
 * ela na mão. A câmera abre numa janela (`LeitorQr`, que cai para a digitação
 * quando não há câmera). O que se lê é conferido ANTES de navegar: um código
 * de amostra (`AM-…`) é perguntado à API, e só abre a amostra se ela
 * existir — senão o aviso fica na própria janela, em vez de uma tela de erro
 * que a tiraria de onde estava. Ali a câmera segue lendo: a próxima etiqueta
 * posta no quadro é conferida — mesmo que chegue enquanto a anterior ainda
 * está sendo conferida: a última lida vence, e a conferência anterior é
 * abortada (Ruling R30). A MESMA etiqueta, parada no quadro, não é
 * entregue de novo (o `LeitorQr` entrega cada código uma vez — Ruling R26);
 * para repeti-la, digita-se o código ou fecha-se e reabre-se a janela. O que
 * não tem cara de amostra é o código patrimonial de um equipamento, e a tela
 * `EquipamentoPorCodigo` já trata o não achado. O texto do campo passa a
 * «Buscar ou ler QR». Fechar a janela sem navegar (Esc, × ou «Cancelar»)
 * devolve o foco ao «Ler QR». No desktop nada muda.
 */

/**
 * As telas que a busca acha pelo nome antigo (onda 353, RN-274).
 *
 * «"Aferição" some de telas, documentos e menus. Fica só como sinônimo na
 *  busca, para quem ainda digitar a palavra antiga» — Humberto, 23/10/2026.
 * Quem digita «aferição», «aferir» ou «calibração interna» acha as duas
 * telas da calibração interna, no grupo «Telas», antes dos registros.
 */
const TELAS_POR_PALAVRA: Array<{ palavra: RegExp; titulo: string; detalhe: string; destino: string }> = [
  { palavra: /aferi|calibra[çc][ãa]o interna|calibrar/i, titulo: 'Calibração interna',
    detalhe: 'Os certificados internos (antes «aferição»)', destino: '/equipamentos/verificacoes' },
  { palavra: /aferi|calibra[çc][ãa]o interna|calibrar/i, titulo: 'Calibração interna em lote',
    detalhe: 'A folha da campanha por categoria (antes «aferição em lote»)',
    destino: '/equipamentos/aferir-massas' },
];

/** O código da etiqueta de amostra: `AM-2026-00001` (ano e sequência). */
const CODIGO_DE_AMOSTRA = /^AM-\d{4}-\d{5}$/i;

interface Achado {
  chave: string;
  grupo: string;
  titulo: string;
  detalhe: string;
  destino: string;
}

const MINIMO = 2;

export function BuscaGlobal() {
  const [termo, setTermo] = useState('');
  const [achados, setAchados] = useState<Achado[] | null>(null);
  const [aberta, setAberta] = useState(false);
  const [foco, setFoco] = useState(0);
  const campo = useRef<HTMLInputElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const ir = useNavigate();
  const { pode } = useSessao();
  const veAmostras = pode('amostra.ver');
  const estreito = useEstreito(640);

  /* A janela do QR (só o celular tem o botão que a abre). */
  const [lendoQr, setLendoQr] = useState(false);
  const [naoAchou, setNaoAchou] = useState<string | null>(null);
  const [erroDoQr, setErroDoQr] = useState<unknown>(null);
  /* A conferência em curso: uma por vez — a leitura nova aborta a anterior —,
     e cancelada se a janela fechar: a resposta que chega depois não pode
     levar a pessoa para outra tela. */
  const conferencia = useRef<AbortController | null>(null);
  /* O «Ler QR», para onde o foco volta quando a janela fecha sem navegar. */
  const botaoQr = useRef<HTMLButtonElement>(null);
  const devolverFoco = useRef(false);

  const fecharQr = useCallback(() => {
    conferencia.current?.abort();
    conferencia.current = null;
    setLendoQr(false);
    setNaoAchou(null);
    setErroDoQr(null);
  }, []);

  /* Fechar sem navegar (Esc, × ou «Cancelar»): o foco volta a quem abriu a
     janela, e não cai no `body`. */
  const cancelarQr = useCallback(() => {
    devolverFoco.current = true;
    fecharQr();
  }, [fecharQr]);

  /* O foco só volta DEPOIS de a janela sair da tela: com o `<dialog>` modal
     aberto, o resto da página é inerte e o `focus()` não pegaria. */
  useEffect(() => {
    if (lendoQr || !devolverFoco.current) return;
    devolverFoco.current = false;
    botaoQr.current?.focus();
  }, [lendoQr]);

  /* Estável (`useCallback`): o `LeitorQr` reabre a câmera quando o `aoLer`
     muda, e um `aoLer` novo a cada render a reabriria a cada render. */
  const aoLerQr = useCallback(async (codigo: string) => {
    /* A última leitura vence (Ruling R30): a etiqueta lida enquanto a
       anterior ainda é conferida aborta a anterior — que sai calada — e é
       conferida no lugar dela. Descartar a nova a deixaria sem conferência
       nenhuma: o `LeitorQr` já a deu por entregue e não a entrega de novo. */
    conferencia.current?.abort();
    setNaoAchou(null);
    setErroDoQr(null);
    if (!CODIGO_DE_AMOSTRA.test(codigo)) {
      fecharQr();
      ir(`/equipamento/codigo/${encodeURIComponent(codigo)}`);
      return;
    }
    const ctl = new AbortController();
    conferencia.current = ctl;
    try {
      await api(`/amostras/codigo/${encodeURIComponent(codigo)}`, { sinal: ctl.signal });
      if (ctl.signal.aborted) return;
      fecharQr();
      ir(`/amostra/${encodeURIComponent(codigo)}`);
    } catch (e) {
      if (ctl.signal.aborted) return;
      if (e instanceof ErroApi && e.status === 404) setNaoAchou(`Nenhuma amostra com o código ${codigo}`);
      else setErroDoQr(e);
    } finally {
      if (conferencia.current === ctl) conferencia.current = null;
    }
  }, [fecharQr, ir]);

  // Ctrl+K / Cmd+K de qualquer lugar da aplicação.
  useEffect(() => {
    const atalho = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        campo.current?.focus();
        campo.current?.select();
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, []);

  // Clique fora fecha a lista.
  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberta(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  useEffect(() => {
    const t = termo.trim();
    if (t.length < MINIMO) { setAchados(null); return; }
    const abortar = new AbortController();
    const atraso = setTimeout(async () => {
      const q = `?busca=${encodeURIComponent(t)}`;
      const opc = { sinal: abortar.signal };
      const nada = Promise.resolve<Record<string, string>[]>([]);
      // `allSettled`: uma fonte fora do ar não pode apagar as outras duas.
      const [am, lot, eq] = await Promise.allSettled([
        veAmostras ? api<Record<string, string>[]>(`/amostras${q}`, opc) : nada,
        veAmostras ? api<Record<string, string>[]>(`/recebimento${q}`, opc) : nada,
        api<Record<string, string>[]>(`/equipamentos${q}`, opc),
      ]);
      if (abortar.signal.aborted) return;
      const r: Achado[] = TELAS_POR_PALAVRA
        .filter((x) => x.palavra.test(t))
        .map((x) => ({ chave: `tela-${x.destino}`, grupo: 'Telas', titulo: x.titulo,
          detalhe: x.detalhe, destino: x.destino }));
      const pegar = (x: PromiseSettledResult<Record<string, string>[]>) =>
        (x.status === 'fulfilled' ? x.value : []).slice(0, 5);

      pegar(am).forEach((a) => r.push({
        chave: `am-${a.id}`, grupo: 'Amostras',
        // Onda 303 (RN-248): acha pela identificação do cliente (a rota já
        // procura nela) e mostra por ela; o código e a OS vêm logo embaixo —
        // a busca mistura amostras de todas as OS. O link continua pelo
        // código, que é o do QR. Funções e não o `<NomeDaAmostra>` porque o
        // item da busca já tem as duas linhas (`b` e `span`) no seu estilo.
        titulo: tituloDaAmostra(daApi(a, true)),
        detalhe: [apoioDaAmostra(daApi(a, true)), a.cliente, a.obra_nome]
          .filter(Boolean).join(' · '),
        destino: `/amostra/${a.codigo}`,
      }));
      pegar(lot).forEach((l) => r.push({
        chave: `lo-${l.id}`, grupo: 'Recebimentos',
        titulo: l.codigo,
        detalhe: [l.cliente, l.decisao && rotulo(l.decisao)].filter(Boolean).join(' · '),
        destino: `/recebimento/${l.id}`,
      }));
      pegar(eq).forEach((e) => r.push({
        chave: `eq-${e.id}`, grupo: 'Equipamentos',
        titulo: e.codigo_patrimonial,
        detalhe: [e.nome, e.status && rotulo(e.status)].filter(Boolean).join(' · '),
        destino: `/equipamento/${e.id}`,
      }));
      setAchados(r);
      setFoco(0);
      setAberta(true);
    }, 250);
    return () => { clearTimeout(atraso); abortar.abort(); };
  }, [termo, veAmostras]);

  const abrir = (a: Achado) => {
    setAberta(false);
    setTermo('');
    ir(a.destino);
  };

  const tecla = (e: React.KeyboardEvent) => {
    if (!achados || achados.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFoco((i) => (i + 1) % achados.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setFoco((i) => (i - 1 + achados.length) % achados.length); }
    if (e.key === 'Enter') { e.preventDefault(); abrir(achados[foco]); }
    if (e.key === 'Escape') setAberta(false);
  };

  let grupoAtual = '';

  return (
    <div className="busca" ref={caixa}>
      <input
        ref={campo}
        type="search"
        value={termo}
        onChange={(e) => setTermo(e.target.value)}
        onFocus={() => achados && setAberta(true)}
        onKeyDown={tecla}
        placeholder={estreito ? 'Buscar ou ler QR'
          : veAmostras
            ? 'Buscar amostra, recebimento ou equipamento…'
            : 'Buscar equipamento…'}
        aria-label="Busca global"
        aria-expanded={aberta}
        role="combobox"
        aria-controls="busca-resultados"
      />
      {/* O «Ler QR», dentro do campo, à direita — só no celular (a folha de
          estilo o esconde acima de 640 px, e o React nem o desenha). */}
      {estreito && (
        <button ref={botaoQr} type="button" className="busca-qr" aria-label="Ler QR da etiqueta"
          onClick={() => { setAberta(false); setLendoQr(true); }}>
          <Icone nome="qr" />
        </button>
      )}
      {/* A janela do leitor, no `body` por portal: dentro do `.topo .busca`
          ela herdaria as regras da faixa escura (`.topo .busca input`,
          `.topo button`) — o campo e os botões saíam em creme sobre o fundo
          claro da janela. Fechada, o `LeitorQr` desmonta e solta a câmera. */}
      {lendoQr && createPortal(
        <Dialogo aberto titulo="Ler QR da etiqueta" aoFechar={cancelarQr}>
          {naoAchou && <Aviso tipo="atencao" titulo={naoAchou} />}
          {erroDoQr ? <ErroDaApi erro={erroDoQr} /> : null}
          <LeitorQr aoLer={aoLerQr} aoCancelar={cancelarQr} />
        </Dialogo>,
        document.body,
      )}
      {aberta && achados && (
        <div className="busca-lista" id="busca-resultados" role="listbox">
          {achados.length === 0 && <p className="nada" style={{ padding: '.6rem .8rem', margin: 0 }}>
            Nada encontrado para “{termo.trim()}”.
          </p>}
          {achados.map((a, i) => {
            const cabeca = a.grupo !== grupoAtual ? (grupoAtual = a.grupo) : null;
            return (
              <div key={a.chave}>
                {cabeca && <div className="busca-grupo">{cabeca}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === foco}
                  className={`busca-item ${i === foco ? 'foco' : ''}`}
                  onMouseEnter={() => setFoco(i)}
                  onClick={() => abrir(a)}
                >
                  <b>{a.titulo}</b>
                  {a.detalhe && <span>{a.detalhe}</span>}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
